import { useState, useCallback, useEffect } from 'react'
import axios from 'axios'

const API = process.env.REACT_APP_API_URL
const MAX_ERRORES = 6
const TECLADO = ['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM']

const COLOR_CATEGORIA = {
  animales: '#3B82F6', comida: '#F59E0B', casa: '#8B5CF6', ropa: '#EC4899',
  trabajo: '#059669', naturaleza: '#22C55E', tecnologia: '#0EA5E9',
  cuerpo: '#EF4444', adjetivos: '#F97316', verbos: '#6366F1'
}
const NOMBRE_CATEGORIA = {
  animales: 'Animales', comida: 'Comida', casa: 'Casa', ropa: 'Ropa',
  trabajo: 'Trabajo', naturaleza: 'Naturaleza', tecnologia: 'Tecnología',
  cuerpo: 'Cuerpo', adjetivos: 'Adjetivos', verbos: 'Verbos'
}
const DIBUJO_AHORCADO = ['🙂', '😐', '😟', '😨', '😰', '😱', '💀']

/* Juego de Ahorcado: se elige una palabra al azar del banco, el
   usuario adivina letra por letra desde un teclado en pantalla,
   con hasta 6 errores permitidos y una pista en español. Se puede
   jugar tantas veces como se quiera. */
export default function AhorcadoJuego({ usuario, onCambio }) {
  const [cargando,    setCargando]    = useState(true)
  const [palabra,     setPalabra]     = useState(null)
  const [adivinadas,  setAdivinadas]  = useState([])
  const [errores,     setErrores]     = useState(0)
  const [estado,      setEstado]      = useState('jugando') // jugando | ganado | perdido
  const [enviando,    setEnviando]    = useState(false)
  const [resultado,   setResultado]   = useState(null)
  const [error,       setError]       = useState(null)

  const nuevaPartida = useCallback(() => {
    setCargando(true)
    setError(null)
    setAdivinadas([])
    setErrores(0)
    setEstado('jugando')
    setResultado(null)
    axios.get(`${API}/juegos/ahorcado/palabra`).then(res => {
      setPalabra(res.data.datos || null)
    }).catch(err => {
      console.error('Error cargando ahorcado:', err)
      setError('No pudimos cargar una palabra. Intenta de nuevo.')
    }).finally(() => setCargando(false))
  }, [])

  useEffect(() => { nuevaPartida() }, [nuevaPartida])

  function letrasUnicas(palabraStr) {
    return Array.from(new Set(palabraStr.toUpperCase().split('')))
  }

  function elegirLetra(letra) {
    if (estado !== 'jugando' || adivinadas.includes(letra)) return
    const nuevasAdivinadas = [...adivinadas, letra]
    setAdivinadas(nuevasAdivinadas)

    const esCorrecta = palabra.palabra.toUpperCase().includes(letra)
    if (esCorrecta) {
      const faltan = letrasUnicas(palabra.palabra).some(l => !nuevasAdivinadas.includes(l))
      if (!faltan) setEstado('ganado')
    } else {
      const nuevosErrores = errores + 1
      setErrores(nuevosErrores)
      if (nuevosErrores >= MAX_ERRORES) setEstado('perdido')
    }
  }

  async function reclamarXp() {
    if (enviando || !usuario?.id) return
    setEnviando(true)
    setError(null)
    try {
      const res = await axios.post(`${API}/juegos/ahorcado/completar`, {
        usuario_id: usuario.id, gano: true, palabra_id: palabra.id
      })
      setResultado(res.data)
      if (onCambio) onCambio()
    } catch (err) {
      console.error('Error guardando resultado de ahorcado:', err)
      setError('No pudimos guardar tu resultado. Intenta de nuevo.')
    } finally {
      setEnviando(false)
    }
  }

  if (cargando) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
        Cargando palabra...
      </div>
    )
  }

  if (!palabra) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--gris-texto)' }}>
        <div style={{ fontSize: 44, marginBottom: 12 }}>🎯</div>
        <p>Todavía no hay palabras de ahorcado cargadas. ¡Vuelve pronto!</p>
      </div>
    )
  }

  const color = COLOR_CATEGORIA[palabra.categoria] || 'var(--azul)'
  const letrasPalabra = palabra.palabra.toUpperCase().split('')
  const terminado = estado === 'ganado' || estado === 'perdido'

  return (
    <div style={{ animation: 'fadeIn .3s ease both', maxWidth: 640 }}>
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800 }}>🎯 Ahorcado</h1>
        <p style={{ color: 'var(--gris-texto)', fontSize: 14, marginTop: 4 }}>
          Adivina la palabra en inglés antes de quedarte sin intentos.
        </p>
      </div>

      {error && (
        <div style={{ background: '#FEE2E2', color: '#B91C1C', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid var(--gris-borde)', borderRadius: 18, padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ background: color, color: '#fff', fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 999 }}>
              {NOMBRE_CATEGORIA[palabra.categoria] || palabra.categoria}
            </span>
            <span style={{ fontSize: 11, color: 'var(--gris-texto)' }}>{palabra.nivel}</span>
          </div>
          <div style={{ fontSize: 26 }}>{DIBUJO_AHORCADO[Math.min(errores, MAX_ERRORES - 1)]}</div>
        </div>

        <div style={{ background: '#F8FAFF', borderRadius: 10, padding: 12, marginBottom: 20 }}>
          <p style={{ fontSize: 12.5, color: 'var(--gris-texto)' }}>💡 Pista: {palabra.pista}</p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
          {letrasPalabra.map((letra, i) => {
            const mostrar = terminado || adivinadas.includes(letra)
            return (
              <div key={i} style={{
                width: 32, height: 40, borderBottom: '3px solid ' + (terminado && estado === 'perdido' ? '#EF4444' : 'var(--azul)'),
                display: 'flex', alignItems: 'flex-end', justifyContent: 'center', fontSize: 20, fontWeight: 800,
                color: mostrar ? '#1A1A2E' : 'transparent'
              }}>
                {letra}
              </div>
            )
          })}
        </div>

        <div style={{ textAlign: 'center', fontSize: 12.5, color: 'var(--gris-texto)', marginBottom: 16 }}>
          Errores: {errores} / {MAX_ERRORES}
        </div>

        {!terminado && (
          <div>
            {TECLADO.map((fila, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 6 }}>
                {fila.split('').map(letra => {
                  const usada = adivinadas.includes(letra)
                  const correcta = usada && palabra.palabra.toUpperCase().includes(letra)
                  let fondo = '#fff', borde = 'var(--gris-borde)', texto = '#1A1A2E'
                  if (usada && correcta) { fondo = '#DCFCE7'; borde = '#22C55E' }
                  else if (usada && !correcta) { fondo = '#FEE2E2'; borde = '#EF4444' }
                  return (
                    <button key={letra} onClick={() => elegirLetra(letra)} disabled={usada}
                      style={{
                        width: 30, height: 36, borderRadius: 8, border: '1.5px solid ' + borde,
                        background: fondo, color: texto, fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                        cursor: usada ? 'default' : 'pointer'
                      }}>
                      {letra}
                    </button>
                  )
                })}
              </div>
            ))}
          </div>
        )}

        {terminado && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>{estado === 'ganado' ? '🎉' : '💀'}</div>
            <h2 style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>
              {estado === 'ganado' ? '¡Ganaste!' : '¡Se acabaron los intentos!'}
            </h2>
            <p style={{ fontSize: 14, color: 'var(--gris-texto)', marginBottom: 18 }}>
              La palabra era: <strong>{palabra.palabra}</strong>
            </p>

            {estado === 'ganado' ? (
              resultado ? (
                <div>
                  <div style={{ background: 'linear-gradient(135deg,#065F46,#059669)', color: '#fff', borderRadius: 14, padding: 18, marginBottom: 18 }}>
                    <div style={{ fontSize: 20, fontWeight: 800 }}>+{resultado.xp_ganado} XP ganados</div>
                  </div>
                  <button onClick={nuevaPartida}
                    style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'var(--azul)', color: '#fff',
                      fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                    🔁 Jugar de nuevo
                  </button>
                </div>
              ) : (
                <button onClick={reclamarXp} disabled={enviando}
                  style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', fontFamily: 'inherit',
                    fontSize: 15, fontWeight: 700, cursor: enviando ? 'not-allowed' : 'pointer',
                    background: enviando ? '#CBD5E1' : 'var(--dorado)', color: '#fff' }}>
                  {enviando ? 'Guardando...' : `Reclamar +${palabra.xp || 5} XP`}
                </button>
              )
            ) : (
              <button onClick={nuevaPartida}
                style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'var(--azul)', color: '#fff',
                  fontFamily: 'inherit', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                🔁 Intentar otra palabra
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
